<template>
  <div :class="['profile-form', { 'is-bare': bare }]">
    <section
      v-for="skill in visibleSkills"
      :key="skill.field"
      class="profile-section"
      :aria-labelledby="`${uid}-${skill.field}`"
    >
      <header :class="['section-header', { 'visually-hidden': bare }]">
        <h3 :id="`${uid}-${skill.field}`">{{ $t(`learnerProfile.${skill.key}Title`) }}</h3>
        <p v-if="!compact">{{ $t(`learnerProfile.${skill.key}Hint`) }}</p>
      </header>
      <div :class="compact ? 'level-choices' : 'option-list'" role="radiogroup" :aria-labelledby="`${uid}-${skill.field}`">
        <button
          v-for="level in levels"
          :key="level"
          type="button"
          role="radio"
          :aria-checked="modelValue[skill.field] === level"
          :aria-label="compact ? `${level} ${$t(`learnerProfile.levelNames.${level}`)}` : undefined"
          :tabindex="modelValue[skill.field] === level || (!modelValue[skill.field] && level === levels[0]) ? 0 : -1"
          :class="[compact ? 'level-choice' : 'list-row', { selected: modelValue[skill.field] === level }]"
          @click="toggleLevel(skill.field, level)"
          @keydown="moveLevel($event, skill.field, level)"
        >
          <template v-if="compact">{{ level }}</template>
          <template v-else>
            <span class="row-text">
              <span class="row-title">
                <span class="level-code">{{ level }}</span>
                {{ $t(`learnerProfile.levelNames.${level}`) }}
              </span>
              <span class="row-description">{{ $t(`learnerProfile.${skill.key}Levels.${level}`) }}</span>
              <span class="row-note">
                {{ $t('learnerProfile.examplePrefix') }}{{ $t(`learnerProfile.${skill.key}Examples.${level}`) }}
              </span>
            </span>
            <span class="material-icons row-check" aria-hidden="true">check</span>
          </template>
        </button>
      </div>
      <div v-if="compact && (modelValue[skill.field] || showEmptyHint)" class="level-detail" aria-live="polite">
        <template v-if="modelValue[skill.field]">
          <p class="level-description"><strong>{{ $t(`learnerProfile.levelNames.${modelValue[skill.field]}`) }}</strong> · {{ $t(`learnerProfile.${skill.key}Levels.${modelValue[skill.field]}`) }}</p>
          <p class="level-example">{{ $t('learnerProfile.examplePrefix') }}{{ $t(`learnerProfile.${skill.key}Examples.${modelValue[skill.field]}`) }}</p>
        </template>
        <p v-else class="level-description">{{ $t('learnerProfile.levelChoiceHint') }}</p>
      </div>
    </section>

    <section v-if="shows('interests')" class="profile-section" :aria-labelledby="`${uid}-interests`">
      <header :class="['section-header', { 'visually-hidden': bare }]">
        <h3 :id="`${uid}-interests`">{{ $t('learnerProfile.interestsTitle') }}</h3>
        <p>{{ $t('learnerProfile.interestsHint') }}</p>
      </header>
      <div class="pill-list">
        <button
          v-for="topic in topics"
          :key="topic"
          type="button"
          :aria-pressed="modelValue.interests.includes(topic)"
          :class="['topic-pill', { selected: modelValue.interests.includes(topic) }]"
          @click="toggleChoice('interests', topic)"
        >
          <span class="material-icons-outlined pill-icon" aria-hidden="true">
            {{ modelValue.interests.includes(topic) ? 'check' : topicIcons[topic] }}
          </span>
          {{ $t(`learnerProfile.topics.${topic}`) }}
        </button>
      </div>
    </section>

    <section v-if="shows('goals')" class="profile-section" :aria-labelledby="`${uid}-goals`">
      <header :class="['section-header', { 'visually-hidden': bare }]">
        <h3 :id="`${uid}-goals`">{{ $t('learnerProfile.goalsTitle') }}</h3>
        <p>{{ $t('learnerProfile.goalsHint') }}</p>
      </header>
      <div class="option-list">
        <button
          v-for="goal in goals"
          :key="goal"
          type="button"
          :aria-pressed="modelValue.goals.includes(goal)"
          :class="['list-row', { selected: modelValue.goals.includes(goal) }]"
          @click="toggleChoice('goals', goal)"
        >
          <span class="material-icons-outlined row-icon" aria-hidden="true">{{ goalIcons[goal] }}</span>
          <span class="row-text">
            <span class="row-title">{{ $t(`learnerProfile.goals.${goal}`) }}</span>
            <span class="row-note">{{ $t(`learnerProfile.goalDescriptions.${goal}`) }}</span>
          </span>
          <span class="material-icons row-check" aria-hidden="true">check</span>
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import { CEFR_LEVELS, LEARNING_GOALS, MATERIAL_TOPICS } from '@/utils/learnerProfile'

let formCount = 0

const TOPIC_ICONS = {
  'daily-life': 'home',
  travel: 'flight',
  food: 'restaurant',
  work: 'work',
  business: 'trending_up',
  tech: 'devices',
  science: 'science',
  news: 'newspaper',
  culture: 'movie',
  history: 'account_balance',
  stories: 'auto_stories',
  health: 'spa',
  sports: 'sports_soccer',
  psychology: 'psychology',
  study: 'school'
}

const GOAL_ICONS = {
  conversation: 'forum',
  media: 'live_tv',
  work: 'business_center',
  abroad: 'flight_takeoff',
  exam: 'assignment'
}

export default {
  name: 'LearnerProfileForm',
  props: {
    modelValue: { type: Object, required: true },
    // 只显示某一部分：listening / speaking / interests / goals
    step: { type: String, required: true },
    // 档案页里由外层分组提供标题和容器，这里只留选项
    bare: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
    showEmptyHint: { type: Boolean, default: true }
  },
  emits: ['update:modelValue'],
  data() {
    formCount += 1
    return {
      uid: `profile-form-${formCount}`,
      levels: CEFR_LEVELS,
      topics: MATERIAL_TOPICS,
      goals: LEARNING_GOALS,
      topicIcons: TOPIC_ICONS,
      goalIcons: GOAL_ICONS,
      skills: [
        { field: 'listeningLevel', key: 'listening' },
        { field: 'speakingLevel', key: 'speaking' }
      ]
    }
  },
  computed: {
    visibleSkills() {
      return this.skills.filter(skill => this.shows(skill.key))
    }
  },
  methods: {
    shows(section) {
      return this.step === section
    },
    update(changes) {
      this.$emit('update:modelValue', { ...this.modelValue, ...changes })
    },
    // 重复点击当前等级保留原值和来源；更换等级时记为自评。
    toggleLevel(field, level) {
      if (this.modelValue[field] === level) return
      this.update({ [field]: level, [field.replace('Level', 'Source')]: 'self' })
    },
    moveLevel(event, field, level) {
      const directions = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }
      let index = this.levels.indexOf(level)
      if (event.key === 'Home') index = 0
      else if (event.key === 'End') index = this.levels.length - 1
      else if (directions[event.key]) index = (index + directions[event.key] + this.levels.length) % this.levels.length
      else return
      event.preventDefault()
      const buttons = event.currentTarget.parentElement.querySelectorAll('[role="radio"]')
      buttons[index].focus()
      this.toggleLevel(field, this.levels[index])
    },
    toggleChoice(field, value) {
      const current = this.modelValue[field]
      this.update({
        [field]: current.includes(value) ? current.filter(item => item !== value) : [...current, value]
      })
    }
  }
}
</script>

<style scoped>
.profile-form {
  max-width: 720px;
}

.section-header {
  margin-bottom: var(--space-5);
}

.section-header h3 {
  margin: 0;
  color: var(--ink-primary);
  font-size: var(--font-size-xl);
  font-weight: 600;
  letter-spacing: -0.015em;
}

.section-header p {
  margin: var(--space-1) 0 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.5;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* 列表：细分隔线、整行可点、选中只在右侧打勾，和系统设置里的单选列表一致 */
.option-list {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background: var(--surface-panel);
  overflow: hidden;
}

.is-bare .option-list {
  border: none;
  border-radius: 0;
  background: transparent;
}

.list-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-4);
  min-height: 56px;
  padding: var(--space-3) var(--space-4);
  border: none;
  background: transparent;
  color: var(--ink-primary);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.list-row + .list-row::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  left: 52px;
  border-top: 1px solid var(--border-subtle);
}

.list-row:hover {
  background: var(--surface-subtle);
}

.row-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.row-title {
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
  font-weight: 500;
}

.row-description {
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.45;
}

.row-note {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  line-height: 1.45;
}

.row-icon {
  width: 20px;
  flex-shrink: 0;
  color: var(--ink-tertiary);
  font-size: 20px;
}

.row-check {
  flex-shrink: 0;
  width: 20px;
  color: var(--signal-active-strong);
  font-size: 20px;
  visibility: hidden;
}

.list-row.selected .row-check {
  visibility: visible;
}

.list-row.selected .row-icon {
  color: var(--signal-active-strong);
}

.level-code {
  margin-right: var(--space-1);
  font-family: var(--font-family-data);
  font-weight: 600;
}

.list-row.selected .level-code {
  color: var(--signal-active-strong);
}

.level-choices {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: var(--space-2);
}

.level-choice {
  min-width: 0;
  min-height: 44px;
  padding: 0;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-sm);
  background: var(--surface-panel);
  color: var(--ink-secondary);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 500;
  cursor: pointer;
}

.level-choice:hover {
  border-color: var(--ink-tertiary);
  color: var(--ink-primary);
}

.level-choice.selected {
  border-color: var(--ink-primary);
  background: var(--ink-primary);
  color: var(--color-text-inverse);
  font-weight: 600;
}

.level-choice:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: 2px;
}

.level-detail {
  margin-top: var(--space-3);
}

.level-description,
.level-example {
  margin: 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

.level-description strong {
  color: var(--ink-primary);
  font-weight: 500;
}

.level-example {
  margin-top: var(--space-1);
}

@media (max-width: 640px) {
  .level-choices {
    gap: var(--space-1);
  }
}

/* 领域：胶囊标签，选中反色并把图标换成勾 */
.pill-list {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.topic-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 var(--space-4) 0 var(--space-3);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-pill);
  background: var(--surface-panel);
  color: var(--ink-primary);
  font: inherit;
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.topic-pill:hover {
  border-color: var(--ink-tertiary);
}

.topic-pill.selected {
  border-color: var(--ink-primary);
  background: var(--ink-primary);
  color: var(--color-text-inverse);
}

.pill-icon {
  color: var(--ink-tertiary);
  font-size: 18px;
}

.topic-pill.selected .pill-icon {
  color: var(--color-text-inverse);
}

.list-row:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: -2px;
}

.topic-pill:focus-visible {
  outline: 2px solid var(--signal-active-strong);
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .list-row,
  .topic-pill {
    transition: none;
  }
}
</style>
